import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Button,
  Chip,
  Link,
  Tabs,
  Tab,
  Stack,
} from '@mui/material';
import {
  ConfirmationNumber,
  Event,
  LocationOn,
  AccessTime,
  CheckCircle,
  History,
  ArrowForward,
  Lock,
  OpenInNew,
} from '@mui/icons-material';
import {
  API_BASE,
  palette,
  pageBackground,
  cardSx,
  primaryButtonSx,
  assetUrl,
  formatPrice,
  formatEventTime,
  fewLeftLabel,
  posterFrameSx,
  posterImgSx,
} from './ticketTheme';
import TicketTermsFooter from './TicketTermsFooter';
import { getEventAccess, formatPublicLocation, openExternalTicketUrl } from './eventAccess';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** Split events into: upcoming (current year, by month), done this year, and past years */
const groupEvents = (events) => {
  const now = new Date();
  const currentYear = now.getFullYear();

  const undated = [];
  const upcomingByMonth = new Map(); // monthIndex -> events
  const doneThisYear = [];
  const pastByYear = new Map(); // year -> events

  events.forEach((event) => {
    if (!event.event_date) {
      undated.push(event);
      return;
    }
    const date = new Date(event.event_date);
    const year = date.getFullYear();

    if (year > currentYear) {
      // Next-year events still belong in "upcoming"; keyed beyond 11 so they sort last
      const key = 12 + (year - currentYear - 1) * 12 + date.getMonth();
      if (!upcomingByMonth.has(key)) upcomingByMonth.set(key, { label: `${MONTH_NAMES[date.getMonth()]} ${year}`, events: [] });
      upcomingByMonth.get(key).events.push(event);
      return;
    }

    if (year < currentYear) {
      if (!pastByYear.has(year)) pastByYear.set(year, []);
      pastByYear.get(year).push(event);
      return;
    }

    if (date < now) {
      doneThisYear.push(event);
    } else {
      const key = date.getMonth();
      if (!upcomingByMonth.has(key)) upcomingByMonth.set(key, { label: `${MONTH_NAMES[key]} ${currentYear}`, events: [] });
      upcomingByMonth.get(key).events.push(event);
    }
  });

  const sortByDate = (a, b) => new Date(a.event_date) - new Date(b.event_date);

  const upcomingGroups = [...upcomingByMonth.entries()]
    .sort(([a], [b]) => a - b)
    .map(([, group]) => ({ ...group, events: group.events.sort(sortByDate) }));

  if (undated.length > 0) {
    upcomingGroups.push({ label: 'Date To Be Announced', events: undated });
  }

  doneThisYear.sort((a, b) => new Date(b.event_date) - new Date(a.event_date));

  const pastGroups = [...pastByYear.entries()]
    .sort(([a], [b]) => b - a)
    .map(([year, list]) => ({
      label: String(year),
      events: list.sort((a, b) => new Date(b.event_date) - new Date(a.event_date)),
    }));

  return { upcomingGroups, doneThisYear, pastGroups };
};

/** Calendar-style date tile (big day number over month) */
const DateTile = ({ dateStr }) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  return (
    <Box
      sx={{
        flexShrink: 0,
        width: 56,
        height: 64,
        borderRadius: 2,
        overflow: 'hidden',
        border: `1px solid ${palette.accentBorder}`,
        textAlign: 'center',
        backgroundColor: palette.surfaceAlt,
      }}
    >
      <Box sx={{ backgroundColor: palette.accent, py: 0.25 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 800, color: palette.onAccent, textTransform: 'uppercase', letterSpacing: 1 }}>
          {date.toLocaleDateString('en-GB', { month: 'short' })}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 22, fontWeight: 800, mt: 0.5, color: palette.text, lineHeight: 1.4 }}>
        {date.getDate()}
      </Typography>
    </Box>
  );
};

const SectionHeading = ({ children, icon }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, mt: 1 }}>
    {icon}
    <Typography
      variant="overline"
      sx={{ color: palette.accent, fontWeight: 800, letterSpacing: 2, fontSize: 13, lineHeight: 1 }}
    >
      {children}
    </Typography>
    <Box sx={{ flex: 1, height: '1px', backgroundColor: palette.border }} />
  </Box>
);

const EventCard = ({ event, done = false, onClick }) => {
  const access = getEventAccess(event);
  const soldOut = access.isInternalTickets && event.is_sold_out;
  const fewLeft = access.isInternalTickets && event.is_few_left;
  const clickable = !done && !soldOut;
  const locationLabel = formatPublicLocation(event, access);
  const showVenueLink = !access.isPrivate && !!event.venue_location_link && !done;

  const handleCtaClick = (e) => {
    if (access.isExternalTickets && access.externalTicketUrl) {
      e.stopPropagation();
      openExternalTicketUrl(access.externalTicketUrl);
    }
  };

  return (
    <Paper
      elevation={0}
      onClick={clickable ? onClick : undefined}
      sx={{
        ...cardSx,
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        cursor: clickable ? 'pointer' : 'default',
        transition: 'border-color 0.2s, transform 0.2s, box-shadow 0.2s',
        ...(clickable && {
          '&:hover': {
            borderColor: palette.accentBorder,
            transform: 'translateY(-2px)',
            boxShadow: '0 12px 32px rgba(0,0,0,0.45)',
          },
        }),
        ...(done && { opacity: 0.85 }),
      }}
    >
      {/* Image — square poster, same crop as the ticket PDF */}
      <Box
        sx={{
          ...posterFrameSx,
          width: { xs: '100%', sm: '33.33%' },
          minWidth: { sm: '33.33%' },
          flexShrink: 0,
          alignSelf: { sm: 'flex-start' },
        }}
      >
        {event.image_url ? (
          <Box
            component="img"
            src={assetUrl(event.image_url)}
            alt={event.title}
            sx={{
              ...posterImgSx,
              filter: done ? 'grayscale(0.85)' : 'none',
            }}
          />
        ) : (
          <Box
            sx={{
              ...posterImgSx,
              backgroundColor: palette.surfaceAlt,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Event sx={{ fontSize: 48, color: palette.textMuted }} />
          </Box>
        )}

        {done && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              backgroundColor: 'rgba(12,13,16,0.45)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Chip
              icon={<CheckCircle sx={{ color: `${palette.success} !important`, fontSize: 18 }} />}
              label="Event Completed"
              sx={{
                backgroundColor: 'rgba(12,13,16,0.85)',
                color: palette.success,
                fontWeight: 700,
                border: `1px solid ${palette.successBorder}`,
                backdropFilter: 'blur(4px)',
              }}
            />
          </Box>
        )}

        {!done && (soldOut || fewLeft || access.isPrivate || access.isFreeEntry) && (
          <Box sx={{ position: 'absolute', top: 10, left: 10 }}>
            {soldOut ? (
              <Chip label="Sold Out" size="small" sx={{ backgroundColor: 'rgba(239,83,80,0.92)', color: '#fff', fontWeight: 700 }} />
            ) : access.isPrivate ? (
              <Chip icon={<Lock sx={{ fontSize: 14, color: '#fff !important' }} />} label="Private Event" size="small" sx={{ backgroundColor: 'rgba(12,13,16,0.88)', color: '#fff', fontWeight: 700 }} />
            ) : access.isFreeEntry ? (
              <Chip label="Free Entry" size="small" sx={{ backgroundColor: 'rgba(93,196,106,0.92)', color: '#0d1f12', fontWeight: 700 }} />
            ) : (
              <Chip label={fewLeftLabel(event)} size="small" sx={{ backgroundColor: 'rgba(255,167,38,0.92)', color: '#1d1503', fontWeight: 700 }} />
            )}
          </Box>
        )}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, p: { xs: 2, sm: 2.5 }, display: 'flex', gap: 2, minWidth: 0 }}>
        {!done && <DateTile dateStr={event.event_date} />}

        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <Typography
            sx={{
              color: palette.text,
              fontWeight: 700,
              fontSize: { xs: '1.05rem', sm: '1.15rem' },
              lineHeight: 1.3,
            }}
          >
            {event.title}
          </Typography>
          <Typography variant="body2" sx={{ color: palette.accent, mb: 0.75, fontWeight: 600 }}>
            {event.event_name}
          </Typography>

          <Stack spacing={0.5} sx={{ mb: 1 }}>
            {event.event_date && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <AccessTime sx={{ fontSize: 15, color: palette.textMuted }} />
                <Typography variant="caption" sx={{ color: palette.textSecondary }}>
                  {new Date(event.event_date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
                  {' · '}{formatEventTime(event.event_date)}
                </Typography>
              </Box>
            )}
            {locationLabel && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <LocationOn sx={{ fontSize: 15, color: palette.textMuted }} />
                {showVenueLink ? (
                  <Link
                    href={event.venue_location_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    sx={{ color: palette.textSecondary, fontSize: 12, textDecoration: 'underline', textDecorationColor: palette.borderStrong, '&:hover': { color: palette.accent } }}
                  >
                    {locationLabel}
                  </Link>
                ) : (
                  <Typography variant="caption" sx={{ color: palette.textSecondary }}>{locationLabel}</Typography>
                )}
              </Box>
            )}
          </Stack>

          {event.description && !done && !access.isPrivate && (
            <Typography
              variant="body2"
              sx={{
                color: palette.textMuted,
                mb: 1,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {event.description}
            </Typography>
          )}

          <Box sx={{ mt: 'auto', pt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
            <Box>
              {done ? (
                <Typography sx={{ color: palette.textMuted, fontWeight: 800, fontSize: '1.1rem' }}>
                  {access.isInternalTickets ? formatPrice(event.ticket_price_pence) : ''}
                </Typography>
              ) : access.isPrivate ? (
                <Typography sx={{ color: palette.textSecondary, fontWeight: 700, fontSize: '0.95rem' }}>
                  Private Event
                </Typography>
              ) : access.isFreeEntry ? (
                <Typography sx={{ color: palette.success, fontWeight: 800, fontSize: '1.1rem' }}>
                  FREE ENTRY
                </Typography>
              ) : access.isExternalTickets ? (
                <Typography variant="caption" sx={{ color: palette.textMuted, display: 'block', fontWeight: 600 }}>
                  Tickets available from the organiser.
                </Typography>
              ) : (
                <>
                  {(event.ticket_types?.length > 1 || event.ticket_price_pence) && (
                    <Typography variant="caption" sx={{ color: palette.textMuted, display: 'block', lineHeight: 1 }}>
                      From
                    </Typography>
                  )}
                  <Typography sx={{ color: palette.text, fontWeight: 800, fontSize: '1.1rem' }}>
                    {formatPrice(event.ticket_price_pence)}
                    {event.platform_fee_enabled && event.platform_fee_pence > 0 && (
                      <Box
                        component="span"
                        sx={{
                          color: palette.textMuted,
                          fontWeight: 600,
                          fontSize: '0.72rem',
                          ml: 0.6,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {event.platform_fee_type === 'per_ticket'
                          ? `+ ${formatPrice(event.platform_fee_pence)} platform fee`
                          : '+ platform fee'}
                      </Box>
                    )}
                  </Typography>
                </>
              )}
            </Box>
            {done ? (
              <Chip
                size="small"
                label="That's a wrap"
                sx={{ backgroundColor: palette.surfaceAlt, color: palette.textMuted, fontWeight: 600, fontStyle: 'italic' }}
              />
            ) : access.isInternalTickets || access.isExternalTickets ? (
              <Button
                size="medium"
                variant="contained"
                disabled={soldOut || (access.isExternalTickets && !access.externalTicketUrl)}
                endIcon={!soldOut && (access.isExternalTickets ? <OpenInNew sx={{ fontSize: 16 }} /> : <ArrowForward sx={{ fontSize: 16 }} />)}
                onClick={handleCtaClick}
                sx={{ ...primaryButtonSx, px: 2.5 }}
              >
                {soldOut ? 'Sold Out' : 'Get Tickets'}
              </Button>
            ) : null}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

const EmptyState = ({ message }) => (
  <Paper elevation={0} sx={{ ...cardSx, p: 6, textAlign: 'center' }}>
    <ConfirmationNumber sx={{ fontSize: 40, color: palette.textMuted, mb: 1.5 }} />
    <Typography sx={{ color: palette.textSecondary }}>{message}</Typography>
  </Paper>
);

const TicketEvents = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);

  const currentYear = new Date().getFullYear();

  useEffect(() => {
    fetch(`${API_BASE}/api/tickets/events`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success) setEvents(data.events);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const { upcomingGroups, doneThisYear, pastGroups } = useMemo(
    () => groupEvents(events),
    [events]
  );

  const handleEventClick = (event) => navigate(`/tickets/${event.id}`);

  return (
    <Box sx={{ ...pageBackground, py: { xs: 3, md: 5 }, px: 2 }}>
      <Box sx={{ maxWidth: 860, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography
            variant="overline"
            sx={{ color: palette.accent, fontWeight: 800, letterSpacing: 3, fontSize: 12 }}
          >
            Live Music · Events
          </Typography>
          <Typography
            variant="h4"
            sx={{ color: palette.text, fontWeight: 800, mt: 0.5, fontSize: { xs: '1.7rem', md: '2.2rem' } }}
          >
            Event Tickets
          </Typography>
          <Typography variant="body1" sx={{ color: palette.textSecondary, mt: 1 }}>
            Grab your spot, secure checkout, instant e-ticket to your inbox
          </Typography>
        </Box>

        {/* Tabs */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              minHeight: 44,
              backgroundColor: palette.surface,
              border: `1px solid ${palette.border}`,
              borderRadius: 99,
              p: 0.5,
              '& .MuiTabs-indicator': { display: 'none' },
              '& .MuiTab-root': {
                minHeight: 36,
                px: 3,
                borderRadius: 99,
                color: palette.textSecondary,
                fontWeight: 700,
                textTransform: 'none',
                transition: 'background-color 0.2s, color 0.2s',
              },
              '& .Mui-selected': {
                color: `${palette.onAccent} !important`,
                backgroundColor: palette.accent,
              },
            }}
          >
            <Tab label={`${currentYear}`} disableRipple />
            <Tab label="Past Events" disableRipple icon={<History sx={{ fontSize: 17 }} />} iconPosition="start" />
          </Tabs>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress sx={{ color: palette.accent }} />
          </Box>
        ) : tab === 0 ? (
          /* ---------- CURRENT YEAR ---------- */
          <>
            {upcomingGroups.length === 0 && doneThisYear.length === 0 ? (
              <EmptyState message={`No events scheduled for ${currentYear} yet — check back soon.`} />
            ) : (
              <>
                {upcomingGroups.length === 0 && (
                  <EmptyState message="No upcoming events right now — check back soon." />
                )}

                {upcomingGroups.map((group) => (
                  <Box key={group.label} sx={{ mb: 4 }}>
                    <SectionHeading>{group.label}</SectionHeading>
                    <Stack spacing={2}>
                      {group.events.map((event) => (
                        <EventCard key={event.id} event={event} onClick={() => handleEventClick(event)} />
                      ))}
                    </Stack>
                  </Box>
                ))}

                {doneThisYear.length > 0 && (
                  <Box sx={{ mt: 6 }}>
                    <SectionHeading icon={<CheckCircle sx={{ fontSize: 18, color: palette.success }} />}>
                      Done &amp; Dusted · {currentYear}
                    </SectionHeading>
                    <Typography variant="body2" sx={{ color: palette.textMuted, mb: 2, mt: -1 }}>
                      The nights we already made happen this year
                    </Typography>
                    <Stack spacing={2}>
                      {doneThisYear.map((event) => (
                        <EventCard key={event.id} event={event} done />
                      ))}
                    </Stack>
                  </Box>
                )}
              </>
            )}
          </>
        ) : (
          /* ---------- PAST YEARS ---------- */
          <>
            {pastGroups.length === 0 ? (
              <EmptyState message="No past events to show yet." />
            ) : (
              pastGroups.map((group) => (
                <Box key={group.label} sx={{ mb: 4 }}>
                  <SectionHeading icon={<History sx={{ fontSize: 18, color: palette.accent }} />}>
                    {group.label}
                  </SectionHeading>
                  <Stack spacing={2}>
                    {group.events.map((event) => (
                      <EventCard key={event.id} event={event} done />
                    ))}
                  </Stack>
                </Box>
              ))
            )}
          </>
        )}

        <TicketTermsFooter />
      </Box>
    </Box>
  );
};

export default TicketEvents;
